import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import MenuIcon from "@mui/icons-material/Menu";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import Button from "components/common/Button";
import LoginButton from "components/user/LoginButton";
import { useAppDispatch, useAppSelector } from "hooks/store";
import {
  toggleCart,
  toggleDrawer,
  toggleGlobalSearchModal,
} from "store/slices/main.slice";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import SearchIcon from "@mui/icons-material/Search";
import { useLocation, useNavigate } from "react-router-dom";
import { useMemo } from "react";
import ReservationDialog from "./ReservationDialog";

const ToolBar = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const cartLength = useAppSelector(({ main }) => main.cart.length);
  const { settings, companyData } = useAppSelector(({ main }) => main);
  console.log("🚀 - ToolBar - settings:", settings);

  const isMyOrderPage = useMemo(
    () => location.pathname === "/myOrders",
    [location.pathname]
  );

  // === ADDED BY SAMPATH – 2026-01-06 ===
  // Keep header compact on very small devices, prevent overflow, and improve spacing/font scaling.
  // (No logic changes, only className/UI improvements)
  const hasCompanyName = !!settings?.ShowCompanyName && !!companyData?.companyName;
  const hasLocation = !!settings?.DisplayLocationOnCheckout;
  // === END SAMPATH ===

  return (
    <div
      className="
        h-[56px] sm:h-[60px]
        px-3 sm:px-4 md:px-6 lg:px-8
        flex items-center justify-between
        shadow-[0px_10px_10px_5px_#00000024]
        z-10 bg-headerbgcolor
      "
    >
      {/* Left Side */}
      <div className="flex items-center min-w-0 flex-1">
        {/* Mobile Menu Icon */}
        <div className="md:hidden shrink-0" onClick={() => dispatch(toggleDrawer())}>
          {/* === ADDED BY SAMPATH – 2026-01-06 === */}
          {/* Bigger tap target + consistent sizing */}
          <div className="h-10 w-10 -ml-2 flex items-center justify-center rounded-lg active:scale-[0.98]">
            <MenuIcon className="text-light-text cursor-pointer" />
          </div>
          {/* === END SAMPATH === */}
        </div>

        {/* Back Arrow for 'My Order' page only */}
        {isMyOrderPage && (
          // === ADDED BY SAMPATH – 2026-01-06 ===
          // Wrap icon for better mobile tap target
          <div className="shrink-0">
            <div className="h-10 w-10 flex items-center justify-center rounded-lg active:scale-[0.98]">
              <ArrowBackIcon
                className="!text-white cursor-pointer"
                onClick={() => navigate(-1)}
              />
            </div>
          </div>
          // === END SAMPATH ===
        )}

        {/* Company Logo or Admin Icon */}
        {settings?.showLogo !== 0 ? (
          <div
            onDoubleClick={() => {
              window.location.href = "/admin/setting/time";
            }}
            className="text-white px-1.5 relative shrink-0"
            onClick={() => {
              // debugger;
              // console.log("Helllo");
              //ewrfdfer
            }}
          >
            <img
              src={companyData?.logoUrl}
              alt=""
              // === ADDED BY SAMPATH – 2026-01-06 ===
              // Prevent logo from forcing wide layout on mobile
              className="h-9 w-9 sm:h-11 sm:w-11 md:h-12 md:w-12 rounded-md mr-2 md:mr-3 object-contain"
              // === END SAMPATH ===
            />
          </div>
        ) : (
          <div
            onDoubleClick={() => {
              window.location.href = "/admin/setting/time";
            }}
            className="text-white px-1.5 relative shrink-0"
          >
            {/* === ADDED BY SAMPATH – 2026-01-06 === */}
            {/* Keep icon aligned and not oversized */}
            <AdminPanelSettingsIcon className="!text-white mr-2 cursor-pointer !text-[22px] sm:!text-[24px]" />
            {/* === END SAMPATH === */}
          </div>
        )}

        {/* Company Text */}
        <div className="min-w-0 flex-1">
          {/* Company Name */}
          {!!settings.ShowCompanyName && !!companyData?.companyName && (
            <a href="/" className="block min-w-0">
              <h1
                // === ADDED BY SAMPATH – 2026-01-06 ===
                // Company name must be black (override theme color) + keep responsive/truncate
                className="
                  text-[14px] sm:text-[16px] md:text-[18px]
                  font-semibold md:font-bold
                  leading-tight
                  !text-black
                  truncate
                "
                // === END SAMPATH ===
              >
                {companyData?.companyName}
              </h1>
            </a>
          )}

          {!!settings.DisplayLocationOnCheckout && (
            <p
              // === ADDED BY SAMPATH – 2026-01-06 ===
              // Make address readable but not space-hungry on mobile
              className="
                text-[11px] sm:text-[12px]
                leading-tight
                text-white/90
                truncate
              "
              // === END SAMPATH ===
              title={`${companyData?.address1 ?? ""} ${companyData?.address2 ?? ""}`}
            >
              {companyData?.address1} {companyData?.address2}
            </p>
          )}

          {/* === ADDED BY SAMPATH – 2026-01-06 === */}
          {/* If neither name nor location is shown, keep height stable (prevents jumpy layout). */}
          {!hasCompanyName && !hasLocation && <div className="h-[2px]" />}
          {/* === END SAMPATH === */}
        </div>
      </div>

      {/* Right Side Actions */}
      <div
        // === ADDED BY SAMPATH – 2026-01-06 ===
        // Prevent buttons from stretching + better spacing on small screens
        className="flex items-center gap-1.5 sm:gap-2 shrink-0"
        // === END SAMPATH ===
      >
        <Button
          // === ADDED BY SAMPATH – 2026-01-06 ===
          // Square icon buttons: consistent size, less empty space, better mobile tap target
          className="bg-white !text-dark-text !px-0 !min-w-[40px] !h-[40px] sm:!min-w-[42px] sm:!h-[42px] rounded-xl relative"
          // === END SAMPATH ===
          onClick={() => dispatch(toggleGlobalSearchModal())}
        >
          <SearchIcon className="!text-black cursor-pointer !text-[20px]" />
        </Button>

        {/* Home Button for 'My Order' page only */}
        {isMyOrderPage && (
          <Button
            // === ADDED BY SAMPATH – 2026-01-06 ===
            className="bg-white !text-dark-text !px-0 !min-w-[40px] !h-[40px] sm:!min-w-[42px] sm:!h-[42px] rounded-xl relative"
            // === END SAMPATH ===
            onClick={() => navigate("/")}
          >
            <HomeIcon className="!text-black cursor-pointer !text-[20px]" />
          </Button>
        )}

        {/* Login Button */}
        {/* === ADDED BY SAMPATH – 2026-01-06 === */}
        {/* Keep LoginButton aligned (doesn't change its internal styling) */}
        <div className="shrink-0">
          <LoginButton />
        </div>
        {/* === END SAMPATH === */}

        {!!settings.CloseReservationOrder &&
          !!settings.ReservationOrderOptions && <ReservationDialog />}

        {/* Shopping Cart Button */}
        <Button
          // === ADDED BY SAMPATH – 2026-01-06 ===
          className="bg-white !text-dark-text !px-0 !min-w-[40px] !h-[40px] sm:!min-w-[42px] sm:!h-[42px] rounded-xl relative"
          // === END SAMPATH ===
          onClick={() => dispatch(toggleCart())}
        >
          {/* === ADDED BY SAMPATH – 2026-01-06 === */}
          {/* Consistent icon sizing */}
          <ShoppingCartIcon className="!text-[20px] sm:!text-[21px]" />
          {/* === END SAMPATH === */}
          {!!cartLength && (
            <div
              // === ADDED BY SAMPATH – 2026-01-06 ===
              // Badge: tighter + centered + uses theme secondary but softened
              className="
                bg-secondary text-light-text
                text-[9px] sm:text-[10px]
                absolute
                h-[16px] w-[16px] sm:h-[18px] sm:w-[18px]
                rounded-full
                flex justify-center items-center
                -top-[4px] -right-[4px] sm:-top-[6px] sm:-right-[6px]
                shadow-sm
              "
              // === END SAMPATH ===
            >
              {cartLength}
            </div>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ToolBar;
